"use client";

import dynamic from "next/dynamic";
import { Loader2 } from "lucide-react";
import { useEffect, useState, useRef, useCallback, memo } from "react";
import { PlotlyTable } from "./PlotlyTable";
import equal from "fast-deep-equal";

// Plotly is loaded lazily — no module-import-time preload.
//
// Why: react-plotly.js + plotly.js-dist-min is ~3-5MB minified, ~10-15MB
// resident after parse. The previous unconditional `import(...)` at
// module top fired the moment ANY component that imports PlotlyChart
// loaded (which transitively means every chat page), even on demos
// with zero plotly outputs. The dynamic import below already lazy-
// loads when the actual <Plot> mounts — and we now further gate that
// on the chart entering the viewport via IntersectionObserver, so a
// notebook with 20 plotly cells doesn't churn 20× plotly mounts at
// once.
const Plot = dynamic(() => import("react-plotly.js"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-[420px] bg-muted/30 rounded-lg">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ),
});

interface PlotlyChartProps {
  data: {
    data: Array<any>;
    layout?: any;
    config?: any;
  };
  noBorder?: boolean;
}


function PlotlyChartComponent({ data, noBorder = false }: PlotlyChartProps) {
  const [themeColors, setThemeColors] = useState({
    popover: "#ffffff",
    border: "#e5e5e5",
    popoverForeground: "#000000",
  });

  // Default hoverlabel settings for theme-aware tooltips
  const defaultHoverlabel = {
    bgcolor: themeColors.popover,
    bordercolor: themeColors.border,
    font: {
      color: themeColors.popoverForeground,
      family: "inherit",
      size: 12,
    },
  };

  // Merge backend hoverlabel with our defaults (backend takes precedence for specific properties)
  const mergedHoverlabel = {
    ...defaultHoverlabel,
    ...data.layout?.hoverlabel,
    font: {
      ...defaultHoverlabel.font,
      ...data.layout?.hoverlabel?.font,
    },
  };

  // Grab the backend-intended width before stripping it, use as design width
  const designWidth = data.layout?.width ?? 700;
  const { width: _width, ...layoutWithoutWidth } = data.layout || {};

  // Measure container width for responsive rendering
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);

  // Viewport-gated mount: a notebook with N plotly cells should not
  // mount N <Plot> instances simultaneously (each one parses ~3-5MB of
  // plotly.js + builds an SVG/canvas tree, peaking the main thread).
  // We render a lightweight placeholder until the container scrolls
  // into view, then mount <Plot> once and keep it mounted (no flap on
  // scroll-out — re-mounting plotly is expensive). 200px rootMargin
  // pre-warms the chart just before it enters the viewport so the
  // viewer never sees the loader.
  const [hasEnteredView, setHasEnteredView] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || hasEnteredView) return;
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      // Old browser fallback — just mount eagerly.
      setHasEnteredView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setHasEnteredView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasEnteredView]);

  const updateContainerW = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerW(el.clientWidth);
  }, []);

  useEffect(() => {
    updateContainerW();
    const ro = new ResizeObserver(updateContainerW);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updateContainerW]);

  // Default layout settings for better appearance with theme-aware tooltips
  const defaultLayout = {
    autosize: true,
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    font: {
      color: "hsl(var(--foreground))",
    },
    // Persist user-driven zoom/pan across Plotly.react() calls. Without
    // this any re-render (resize-observer tick, window resize, layout
    // prop spread) triggers a relayout that auto-recomputes axis
    // ranges and wipes out the box-zoom the user just performed —
    // which made the double-click-to-reset gesture a no-op (nothing
    // to reset). The constant value can be overridden by backend
    // layout via `layoutWithoutWidth` if a chart explicitly opts out.
    uirevision: "user-controlled",
    ...layoutWithoutWidth,
    hoverlabel: mergedHoverlabel,
  };

  // Default config settings
  const defaultConfig = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    ...data.config,
  };
  const plotHeight = defaultLayout.height ?? 420;

  // NOTE: Previously this useEffect fired `window.dispatchEvent(new Event("resize"))`
  // four times on every `data` prop change (rAF + 150ms + 500ms + 1000ms timeouts) to
  // poke Plotly into a relayout pass. With N PlotlyChart instances mounted (canvas
  // mini-previews, modals, carousels, replay alert nodes) every data change cascaded
  // into 4×N GLOBAL resize events. Each one re-fired:
  //   • every other PlotlyChart's container ResizeObserver (N×N work)
  //   • BuddyOverlay's window resize listener (rAF rect recompute)
  //   • every dashboard iframe's ResizeObserver → parent postMessage ping-pong
  //   • ReactFlow's internal viewport observer
  //   • toast.tsx, ChartCarousel, FundAgentResultsCarousel, DashboardView observers
  // On Apple Silicon this kept the compositor pinned at 100% during replay (visible
  // as a sustained GPU plateau in `powermetrics` / GPU History). The container's own
  // ResizeObserver (`updateContainerW` above) already tracks size; CSS-scale handles
  // the visual fit. Plotly's native `useResizeHandler` prop on <Plot/> below now
  // owns the chart's internal layout sync — no global event broadcasting needed.


  // If the payload contains only Plotly table traces, render our custom table
  const traces = Array.isArray(data?.data) ? data.data : [];
  const tableTraces = traces.filter((t: any) => t && t.type === "table" && t.header && t.cells);
  const nonTableTraces = traces.filter((t: any) => !t || t.type !== "table");
  if (tableTraces.length > 0 && nonTableTraces.length === 0) {
    return (
      <div
        className={noBorder ? "w-full max-w-full" : "w-full max-w-full rounded-lg overflow-hidden bg-card border border-border"}
      >
        <div className="w-full h-full">
          {/* Use noBorder on the inner table to avoid double borders */}
          <PlotlyTable trace={tableTraces[0]} layout={data.layout} noBorder={true} />
        </div>
      </div>
    );
  }

  // Width-driven aspect-preserving scale: render the chart at its
  // recorded design size (designWidth × plotHeight) inside a fixed-
  // size inner box, then CSS-scale that box uniformly so the chart
  // exactly matches the container width. Cell height = plotHeight ×
  // scale, so the cell grows/shrinks vertically to keep the chart's
  // aspect ratio intact. This is "proper scaling": no distortion,
  // no letterbox, no forced height — width is the only constraint.
  //
  // Why CSS-scale instead of Plotly responsive: fonts, axis ticks,
  // annotations and trace markers all scale uniformly with the chart
  // (the alternative — `autosize: true` — keeps fonts at native px
  // size, which makes recorded chart designs look broken at small
  // sizes because labels collide with each other and with the chart
  // borders).
  const scale = containerW > 0 ? containerW / designWidth : 1;
  const displayHeight = plotHeight * scale;

  return (
    <div
      ref={containerRef}
      className={noBorder ? "w-full max-w-full overflow-hidden" : "w-full max-w-full rounded-lg overflow-hidden bg-card border border-border"}
      style={{ height: displayHeight }}
    >
      <div
        style={{
          width: designWidth,
          height: plotHeight,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {hasEnteredView ? (
          <Plot
            data={data.data}
            layout={{ ...defaultLayout, autosize: false, width: designWidth, height: plotHeight }}
            config={defaultConfig}
            className="w-full"
            style={{ width: designWidth, height: plotHeight }}
            // useResizeHandler replaces the old global window-dispatchEvent storm
            // (see comment block above the Plot wrapper). Plotly's own internal
            // resize observer attaches to the chart's element and only relays
            // out THIS chart when its container actually changes size. No
            // cascading cross-chart fan-out.
            useResizeHandler
          />
        ) : (
          // Pre-mount placeholder — same dimensions as the real chart so
          // the page layout doesn't shift when <Plot> takes over. No
          // spinner because IntersectionObserver fires ~200px before the
          // chart enters the viewport, so the swap is invisible to the
          // viewer scrolling through the notebook.
          <div
            aria-hidden
            style={{ width: designWidth, height: plotHeight }}
            className="bg-muted/10 rounded-lg"
          />
        )}
      </div>
    </div>
  );
}

// Memoize with deep equality check to prevent re-renders when data hasn't changed
export const PlotlyChart = memo(PlotlyChartComponent, (prevProps, nextProps) => {
  return (
    equal(prevProps.data, nextProps.data) &&
    prevProps.noBorder === nextProps.noBorder
  );
});
